import { DocumentReference, Timestamp } from "firebase/firestore";
import moment from "moment";

import { User } from "../auth";
import Document from "../general/document";
import Customer from "./customer";
import Employee from "./employee";
import HourType from "./hourtype";
import Location from "./location";
import Product from "./product";
import Vehicle from "./vehicle";

export type WorkOrderDayNight = "day" | "night";

export interface WorkOrderData {
  createdBy: string;
  createdAt: Timestamp;
  startAt: Timestamp;
  endAt: Timestamp;
  daynight: WorkOrderDayNight;
  employee: DocumentReference;
  customer: DocumentReference;
  vehicle: DocumentReference;
  location: DocumentReference;
  product: DocumentReference;
  hourType: DocumentReference;
  remarks?: string;
  downloadedAt?: Timestamp;
  syncedAt?: Timestamp;
  syncError?: string;
}

class WorkOrder extends Document<WorkOrderData> {
  private _user?: User;
  private _employee?: Employee;
  private _customer?: Customer;
  private _vehicle?: Vehicle;
  private _location?: Location;
  private _product?: Product;
  private _hourType?: HourType;

  get user() {
    if (this._user?.id !== this.data.createdBy) {
      this._user = new User(`/user/${this.data.createdBy}`, {}, this.store);
    }
    return this._user;
  }

  get startAt() {
    return this.data.startAt?.toDate?.();
  }
  get endAt() {
    return this.data.endAt?.toDate?.();
  }

  get date() {
    return moment(this.startAt).calendar();
  }
  get dateDay() {
    return moment(this.startAt).format("D");
  }
  get dateMonth() {
    return moment(this.startAt).format("MMM").replace(".", "");
  }

  get startTime() {
    return moment(this.startAt).format("H:mm");
  }

  get endTime() {
    return moment(this.endAt).format("H:mm");
  }

  get duration() {
    const startAt = moment(this.startAt);
    const endAt = moment(this.endAt);
    if (endAt.isBefore(startAt)) {
      endAt.add(24, "hour");
    }
    return moment.duration(endAt.diff(startAt));
  }

  get hours() {
    const duration = this.duration;
    const hours = `${duration.hours()}h`;
    const minutes = duration.minutes();
    if (minutes) return `${hours}${minutes}m`;
    return hours;
  }

  get dayNight() {
    return this.data.daynight;
  }

  get employee() {
    if (this._employee?.ref !== this.data.employee) {
      this._employee = new Employee(this.data.employee, {}, this.store);
    }
    return this._employee;
  }

  get customer() {
    if (this._customer?.ref !== this.data.customer) {
      this._customer = new Customer(this.data.customer, {}, this.store);
    }
    return this._customer;
  }

  get vehicle() {
    if (this._vehicle?.ref !== this.data.vehicle) {
      this._vehicle = new Vehicle(this.data.vehicle, {}, this.store);
    }
    return this._vehicle;
  }

  get location() {
    if (this._location?.ref !== this.data.location) {
      this._location = new Location(this.data.location, {}, this.store);
    }
    return this._location;
  }

  get product() {
    if (this._product?.ref !== this.data.product) {
      this._product = new Product(this.data.product, {}, this.store);
    }
    return this._product;
  }

  get hourType() {
    if (this._hourType?.ref !== this.data.hourType) {
      this._hourType = new Product(this.data.hourType, {}, this.store);
    }
    return this._hourType;
  }

  get remarks() {
    return this.data.remarks;
  }

  get downloadedAt() {
    return this.data.downloadedAt?.toDate();
  }

  get syncedAt() {
    return this.data.syncedAt?.toDate();
  }
  get syncError() {
    return this.data.syncError;
  }
}

export default WorkOrder;
